// This is the entry point for the home component
import React, { FC, useCallback, useEffect, useState, useContext } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Styling
import injectSheet from 'react-jss';
import CheckBox from 'Components/CheckBox';
import { Accordion } from 'Components/Accordion';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { areEqualObjects } from 'Utils/areEqual';
import withErrorBoundary from 'HOC/ErrorBoundary';
import styleSheet from './styles';

// Classes
import {
  VisitorTypeReducerState,
  ProspectTree,
} from './classes/visitorType.classes';

// Components
import VisitorTypeProspectTree from './VisitorTypeProspectTree';

// Actions
import {
  setProspectiveStudentsValue,
  setParentsOfProspectsValue,
  setCurrentStudentValue,
  setAlumniValue,
  setFacultyValue,
  setGuidanceCounselorValue,
  setVeteranValue,
  initializeVisitorTypeState,
} from './actions/visitorType.actions';

interface Props {
  classes: any;
}

// Static style objects
const marginLeft22 = { marginLeft: '22px' };
const marginLeft21 = { marginLeft: '21px' };
const margin817 = { margin: '8px 0 17px' };
const margin08 = { margin: '0 0 8px' };
const margin017 = { margin: '0 0 17px' };

const VisitorType: FC<Props> = (props: Props) => {
  const {
    prospectiveStudents,
    parentsOfProspects,
    currentStudent,
    alumni,
    faculty,
    guidanceCounselor,
    veteran,
  } = useSelector(
    (selectors: any) => selectors.filter.demographics.visitorType,
    areEqualObjects
  );
  const { prospectiveStudentsQuery, parentsOfProspectsQuery, otherQuery } =
    useSelector((selectors: any) => selectors.filter.query, areEqualObjects);
  const dispatch = useDispatch();

  const [prospectiveStudentsAccordion, setProspectiveStudentsAccordion] =
    useState(prospectiveStudentsQuery !== '');
  const [parentsofProspectsAccordion, setParentsOfProspectsAccordion] =
    useState(parentsOfProspectsQuery !== '');
  const [otherAccordion, setOtherAccordion] = useState(otherQuery !== '');

  const {
    actions: { filterTrack },
  } = useContext(ProductAnalyticsContext);

  const { classes } = props;

  useEffect(() => {
    if (prospectiveStudentsQuery !== '')
      setProspectiveStudentsAccordion(prospectiveStudentsQuery !== '');
  }, [prospectiveStudentsQuery]);

  useEffect(() => {
    if (parentsOfProspectsQuery !== '')
      setParentsOfProspectsAccordion(parentsOfProspectsQuery !== '');
  }, [parentsOfProspectsQuery]);

  useEffect(() => {
    if (otherQuery !== '') setOtherAccordion(otherQuery !== '');
  }, [otherQuery]);

  // The callback controller for ProspectTrees specifically.
  // Contains the logic for which function to call.
  // call from another callback.
  const prospectTreeCallback = (
    treeIdentifier: string,
    propertyIdentifier: string
  ) => {
    switch (treeIdentifier) {
      case 'prospectiveStudents':
        setProspectiveStudentsValue(
          dispatch,
          prospectiveStudents,
          propertyIdentifier
        );
        break;
      case 'parentsOfProspects':
        setParentsOfProspectsValue(
          dispatch,
          parentsOfProspects,
          propertyIdentifier
        );
        break;
      default:
        break;
    }
  };

  // The callback controller for prospectiveStudents.
  // pass to a checkbox like so: useCallback(callback, [prospectTree]).
  // To ensure the function is not remade every render, cascading
  // to lower components in the tree.  All VisitorType keys will have
  // a unique string array identifier (the keyname for the outer level properties)
  const propsectiveStudentsCallback = (e: any) => {
    const { id, value } = e.target;

    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.ProspectiveStudent.${id}`,
      filterValue: value,
      addToStore: true,
    });
    prospectTreeCallback('prospectiveStudents', id);
  };

  // The callback controller for parentsOfProspects.
  // pass to a checkbox like so: useCallback(callback, [prospectTree]).
  // To ensure the function is not remade every render, cascading
  // to lower components in the tree.  All VisitorType keys will have
  // a unique string array identifier (the keyname for the outer level properties)
  const parentsOfProspectsCallback = (e: any) => {
    const { id, value } = e.target;

    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.ParentOfProspect.${id}`,
      filterValue: value,
      addToStore: true,
    });
    prospectTreeCallback('parentsOfProspects', e.target.id);
  };

  // The callback controller for all the children of "Other".
  // Contains the logic for which function to call.
  // pass to a checkbox like so: useCallback(callback, [currentStudent]).
  // To ensure the function is not remade every render, cascading
  // to lower components in the tree.  All VisitorType keys will have
  // a unique string array identifier (the keyname for the outer level properties)
  const otherCallback = (e: any) => {
    const { id, value } = e.target;

    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.Other.${id}`,
      filterValue: value,
      addToStore: true,
    });

    switch (id) {
      case 'currentStudent':
        setCurrentStudentValue(dispatch, !currentStudent);
        break;
      case 'alumni':
        setAlumniValue(dispatch, !alumni);
        break;
      case 'faculty':
        setFacultyValue(dispatch, !faculty);
        break;
      case 'guidanceCounselor':
        setGuidanceCounselorValue(dispatch, !guidanceCounselor);
        break;
      case 'veteran':
        setVeteranValue(dispatch, !veteran);
        break;
      default:
        break;
    }
  };

  const accordionProspectiveStudent = () => {
    setProspectiveStudentsAccordion(!prospectiveStudentsAccordion);
  };
  const accordionProspectiveStudentSelectAll = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.ProspectiveStudent`,
      filterValue: 'All Selected',
      addToStore: true,
    });
    const newProspectiveStudents = new ProspectTree(
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true
    );
    const newVisitorTypeState = new VisitorTypeReducerState(
      newProspectiveStudents,
      parentsOfProspects,
      currentStudent,
      alumni,
      faculty,
      guidanceCounselor,
      veteran
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };
  const accordionProspectiveStudentClear = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.ProspectiveStudent`,
      filterValue: 'All Cleared',
      addToStore: true,
    });
    const newProspectiveStudents = new ProspectTree();
    const newVisitorTypeState = new VisitorTypeReducerState(
      newProspectiveStudents,
      parentsOfProspects,
      currentStudent,
      alumni,
      faculty,
      guidanceCounselor,
      veteran
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };

  const accordionParentsOfProspects = () => {
    setParentsOfProspectsAccordion(!parentsofProspectsAccordion);
  };

  const accordionParentsOfProspectsSelectAll = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.ParentOfProspect`,
      filterValue: 'All Selected',
      addToStore: true,
    });
    const newParentsOfProspects = new ProspectTree(
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true
    );
    const newVisitorTypeState = new VisitorTypeReducerState(
      prospectiveStudents,
      newParentsOfProspects,
      currentStudent,
      alumni,
      faculty,
      guidanceCounselor,
      veteran
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };
  const accordionParentsOfProspectsClear = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.ParentOfProspect`,
      filterValue: 'All Cleared',
      addToStore: true,
    });
    const newParentsOfProspects = new ProspectTree();
    const newVisitorTypeState = new VisitorTypeReducerState(
      prospectiveStudents,
      newParentsOfProspects,
      currentStudent,
      alumni,
      faculty,
      guidanceCounselor,
      veteran
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };

  const accordionOther = () => {
    setOtherAccordion(!otherAccordion);
  };

  const accordionOtherSelectAll = () => {
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.Other`,
      filterValue: 'All Selected',
      addToStore: true,
    });
    const newVisitorTypeState = new VisitorTypeReducerState(
      prospectiveStudents,
      parentsOfProspects,
      true,
      true,
      true,
      true,
      true
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };

  const accordionOtherClear = () => {
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Demographics',
      filterName: `VisitorType.Other`,
      filterValue: 'All Cleared',
      addToStore: true,
    });
    const newVisitorTypeState = new VisitorTypeReducerState(
      prospectiveStudents,
      parentsOfProspects
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };

  const isEmptyProspectiveStudentsQuery = prospectiveStudentsQuery !== '';
  const useProspectiveStudentsSelectAllButton =
    !prospectiveStudents.hsStudent ||
    !prospectiveStudents.hsFreshman ||
    !prospectiveStudents.hsSophomore ||
    !prospectiveStudents.hsJunior ||
    !prospectiveStudents.hsSenior ||
    !prospectiveStudents.hsGraduate ||
    !prospectiveStudents.collegeGraduate ||
    !prospectiveStudents.collegeStudent ||
    !prospectiveStudents.adultLearner ||
    !prospectiveStudents.middleSchooler;

  const isEmptyParentsOfProspectsQuery = parentsOfProspectsQuery !== '';
  const useParentsOfProspectsSelectAllButton =
    !parentsOfProspects.hsStudent ||
    !parentsOfProspects.hsFreshman ||
    !parentsOfProspects.hsSophomore ||
    !parentsOfProspects.hsJunior ||
    !parentsOfProspects.hsSenior ||
    !parentsOfProspects.hsGraduate ||
    !parentsOfProspects.collegeStudent ||
    !parentsOfProspects.collegeGraduate ||
    !parentsOfProspects.middleSchooler;

  const isEmptyOtherQuery = otherQuery !== '';
  const useOtherQuerySelectAllButton =
    !currentStudent || !alumni || !faculty || !guidanceCounselor || !veteran;

  return (
    <>
      <Accordion
        title="Prospective Student"
        id="prospectiveStudents"
        isOpen={prospectiveStudentsAccordion}
        accordionCallback={accordionProspectiveStudent}
        useTopDivider={false}
        useBottomDivider={false}
        accordionType="Section"
        isActive={isEmptyProspectiveStudentsQuery}
        useClearButton={isEmptyProspectiveStudentsQuery}
        useSelectAllButton={useProspectiveStudentsSelectAllButton}
        selectAll={accordionProspectiveStudentSelectAll}
        clear={accordionProspectiveStudentClear}
        overrideStyles={marginLeft22}
        overrideStylesChildContainer={marginLeft21}
      >
        <div className={classes.container}>
          <VisitorTypeProspectTree
            prospectTree={prospectiveStudents}
            callback={useCallback(propsectiveStudentsCallback, [
              prospectiveStudents,
            ])}
            showAdultLearner
          />
        </div>
      </Accordion>
      <Accordion
        title="Parent of Prospect"
        id="parentsOfProspects"
        isOpen={parentsofProspectsAccordion}
        accordionCallback={accordionParentsOfProspects}
        useTopDivider={false}
        useBottomDivider={false}
        accordionType="Section"
        isActive={isEmptyParentsOfProspectsQuery}
        useClearButton={isEmptyParentsOfProspectsQuery}
        useSelectAllButton={useParentsOfProspectsSelectAllButton}
        selectAll={accordionParentsOfProspectsSelectAll}
        clear={accordionParentsOfProspectsClear}
        overrideStyles={marginLeft22}
        overrideStylesChildContainer={marginLeft21}
      >
        <div className={classes.container}>
          <VisitorTypeProspectTree
            prospectTree={parentsOfProspects}
            callback={useCallback(parentsOfProspectsCallback, [
              parentsOfProspects,
            ])}
            showAdultLearner={false}
          />
        </div>
      </Accordion>
      <Accordion
        title="Other"
        id="other"
        isOpen={otherAccordion}
        accordionCallback={accordionOther}
        useTopDivider={false}
        useBottomDivider={false}
        accordionType="Section"
        isActive={isEmptyOtherQuery}
        useClearButton={isEmptyOtherQuery}
        useSelectAllButton={useOtherQuerySelectAllButton}
        selectAll={accordionOtherSelectAll}
        clear={accordionOtherClear}
        overrideStyles={marginLeft22}
        overrideStylesChildContainer={marginLeft21}
      >
        <div className={classes.container}>
          <CheckBox
            label="Current Student"
            id="currentStudent"
            value={currentStudent}
            isChecked={currentStudent}
            onClick={useCallback(otherCallback, [currentStudent])}
            overrideStyles={margin817}
          />
          <CheckBox
            label="Alumni"
            id="alumni"
            value={alumni}
            isChecked={alumni}
            onClick={useCallback(otherCallback, [alumni])}
            overrideStyles={margin017}
          />
          <CheckBox
            label="Faculty"
            id="faculty"
            value={faculty}
            isChecked={faculty}
            onClick={useCallback(otherCallback, [faculty])}
            overrideStyles={margin017}
          />
          <CheckBox
            label="Guidance Counselor"
            id="guidanceCounselor"
            value={guidanceCounselor}
            isChecked={guidanceCounselor}
            onClick={useCallback(otherCallback, [guidanceCounselor])}
            overrideStyles={margin017}
          />
          <CheckBox
            label="Veteran"
            id="veteran"
            value={veteran}
            isChecked={veteran}
            onClick={useCallback(otherCallback, [veteran])}
            overrideStyles={margin08}
          />
        </div>
      </Accordion>
    </>
  );
};

export default withErrorBoundary(injectSheet(styleSheet)(VisitorType));
